import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChefHat,
  Phone,
  ShoppingCart,
  ArrowLeft,
  Home,
  Utensils,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import MobileMenu from '@/features/storefront/components/MobileMenu';
import { PreviousOrdersButton } from '@/features/storefront/components/PreviousOrdersButton';
import { cn } from '@/shared/lib/utils';

export type PublicNavbarVariant = 'landing' | 'order' | 'subpage';

export interface PublicNavbarProps {
  restaurantId: string;
  restaurantName: string;
  logoUrl?: string;
  contactPhone?: string;
  variant: PublicNavbarVariant;
  /** Page title for subpage variant (e.g. "Checkout", legal page title) */
  title?: string;
  /** Back button destination. Default: landing for subpage, /menu for menu flow */
  backHref?: string;
  /** Cart item count - shows cart button when defined */
  cartCount?: number;
  /** Optional status badge (e.g. "Open", "Closed") */
  storeStatus?: { type: string; message: string };
  /** Optional slot below main nav (e.g. search bar for MenuPage) */
  trailing?: React.ReactNode;
  /** Optional class for the header container */
  className?: string;
}

const PublicNavbar = ({
  restaurantId,
  restaurantName,
  logoUrl,
  contactPhone,
  variant,
  title,
  backHref,
  cartCount = 0,
  storeStatus,
  trailing,
  className,
}: PublicNavbarProps) => {
  const { t } = useTranslation();

  const defaultBackHref =
    variant === 'subpage'
      ? `/${restaurantId}`
      : variant === 'order'
        ? `/${restaurantId}`
        : undefined;

  const href = backHref ?? defaultBackHref;

  const isLanding = variant === 'landing';
  const isOrder = variant === 'order';
  const isSubpage = variant === 'subpage';

  return (
    <header
      className={cn(
        'sticky top-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border safe-area-top',
        className
      )}
    >
      <div className="max-w-7xl mx-auto px-2 xs:px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5">
        {/* Main row */}
        <div className="flex items-start sm:items-center justify-between gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
          {/* Left */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 sm:gap-2.5 min-w-0 flex-1">
            <div className="flex items-center gap-2.5 min-w-0">
              {(isOrder || isSubpage) && href ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="min-h-[44px] min-w-[44px] h-9 w-9 sm:h-9 sm:w-9 rounded-xl shrink-0 hover:bg-muted"
                  asChild
                >
                  <Link to={href} aria-label={t('common.back')}>
                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                </Button>
              ) : null}

              <div
                className={cn(
                  'flex items-center gap-2 sm:gap-3 min-w-0 flex-1',
                  (isOrder || isSubpage) && 'flex-1'
                )}
              >
                <Link
                  to={`/${restaurantId}`}
                  className="shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 rounded-xl"
                  aria-label={restaurantName}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={restaurantName}
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl object-cover"
                      loading="eager"
                      decoding="async"
                      width="40"
                      height="40"
                    />
                  ) : (
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-card border border-border flex items-center justify-center">
                      <ChefHat className="w-4 h-4 sm:w-5 sm:h-5 text-foreground" />
                    </div>
                  )}
                </Link>
                <div className="min-w-0 flex-1">
                  {isSubpage && title ? (
                    <>
                      <span className="font-semibold text-foreground truncate block text-sm sm:text-base">
                        {title}
                      </span>
                      <span className="text-xs text-muted-foreground truncate block">
                        {restaurantName}
                      </span>
                    </>
                  ) : (
                    <Link
                      to={`/${restaurantId}`}
                      className="font-semibold text-base sm:text-lg text-foreground truncate block hover:underline underline-offset-2"
                    >
                      {restaurantName}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Center - Nav links (landing only, desktop) */}
          {isLanding && (
            <nav className="hidden lg:flex items-center gap-1">
              <a
                href="#about"
                className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              >
                {t('public.about')}
              </a>
              <a
                href="#gallery"
                className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              >
                {t('public.gallery')}
              </a>
              <Link
                to={`/${restaurantId}/menu`}
                className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              >
                {t('public.menu')}
              </Link>
            </nav>
          )}

          {/* Right */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <LanguageSwitcher />

            <PreviousOrdersButton restaurantId={restaurantId} />

            {/* Cart - landing & order flow, to the right of language, with count when items */}
            {(isLanding || isOrder || isSubpage) && (
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'relative min-h-[44px] min-w-[44px] h-9 w-9 sm:h-9 sm:w-9 rounded-xl shrink-0 hover:bg-muted',
                  cartCount > 0 && 'bg-primary/10 text-primary hover:bg-primary/15'
                )}
                asChild
              >
                <Link to={`/${restaurantId}/cart`} aria-label={t('public.cart')}>
                  <ShoppingCart className="w-4 h-4" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </Button>
            )}

            {/* Landing actions - desktop */}
            {isLanding && (
              <div className="hidden md:flex items-center gap-2">
                {contactPhone && (
                  <a
                    href={`tel:${contactPhone}`}
                    className="hidden lg:inline-flex"
                    aria-label={`${t('landing.call')} ${restaurantName}`}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-muted text-foreground"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      {t('landing.call')}
                    </Button>
                  </a>
                )}
                <Button
                  className="bg-foreground text-background hover:bg-foreground/90 shadow-md"
                  size="sm"
                  asChild
                >
                  <Link to={`/${restaurantId}/menu`} aria-label={t('landing.orderFood')}>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    <span className="hidden lg:inline">{t('landing.order')}</span>
                  </Link>
                </Button>
              </div>
            )}

            {/* Mobile menu - landing & order */}
            {(isLanding || isOrder) && (
              <div className="md:hidden">
                <MobileMenu
                  restaurantName={restaurantName}
                  phone={contactPhone}
                  sections={
                    isOrder
                      ? [
                        { id: 'home', label: t('public.home', 'Home'), icon: Home, href: `/${restaurantId}` },
                        { id: 'menu', label: t('public.menu'), icon: Utensils, href: `/${restaurantId}/menu` },
                        { id: 'cart', label: t('public.cart'), icon: ShoppingCart, href: `/${restaurantId}/cart` },
                      ]
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        </div>


        {/* Trailing slot (e.g. search) */}
        {trailing && <div className="mt-2.5">{trailing}</div>}
      </div>
    </header>
  );
};

export default PublicNavbar;
